/**
 * Sync queue for submission writes (autosave PATCH + submit). Read requests never go
 * through here. See .claude/skills/farmer-offline-sync/SKILL.md §3 — the four rules below
 * are copied straight from there because they're load-bearing, not just style:
 *
 *   1. Sequential, in creation order (parallel sends can let an old patch clobber a new one).
 *   2. Merge adjacent patchAnswers ops for the same submission (shallow merge, cut at a
 *      'submit' op).
 *   3. `navigator.onLine` is a hint, not a guarantee — real failure detection is
 *      try/catch + status code.
 *   4. Do not infinitely retry 4xx business errors (409 CONFLICT, 422) — surface them and
 *      keep the draft, don't spin forever.
 */
import { getDb } from './db.js';
import * as api from '../api-client.js';

const listeners = new Set();
let flushing = false;
let flushTimer = null;

/** Các `kind` mà `send()` biết cách gửi. Bất cứ thứ gì khác là op hỏng. */
const KNOWN_KINDS = new Set(['patchAnswers', 'submit', 'changeRequest']);

/**
 * Lỗi do chính bản ghi op hỏng, không phải do mạng — ví dụ một op còn sót trong
 * IndexedDB từ phiên bản app cũ với `kind` mà bản hiện tại không còn hiểu.
 *
 * Phải tách bạch với lỗi mạng: `isNetworkError()` mặc định coi lỗi lạ là tạm
 * thời (thà giữ lại còn hơn mất dữ liệu), mà `flush()` gặp lỗi tạm thời thì
 * `return` ngay để giữ đúng thứ tự gửi. Không có lớp lỗi này thì một op hỏng
 * đứng đầu hàng sẽ chặn vĩnh viễn mọi op phía sau — autosave im lặng ngừng
 * chạy và không có gì báo cho người dùng biết.
 */
export class PermanentOpError extends Error {
  constructor(message) {
    super(message);
    this.name = 'PermanentOpError';
  }
}

function notify() {
  listeners.forEach((fn) => {
    try {
      fn();
    } catch (err) {
      console.error('queue listener error', err);
    }
  });
}

/** Subscribe to queue changes (for the sync-status indicator). Returns an unsubscribe fn. */
export function onQueueChange(fn) {
  listeners.add(fn);
  return () => listeners.delete(fn);
}

const prunedListeners = new Set();

/**
 * Báo khoá máy chủ vừa xoá vì câu đang ẩn (`prunedKeys`) khi một op autosave gửi lại từ hàng
 * đợi thành công. Màn nhập đang mở phiếu đó phải bỏ chúng khỏi trạng thái của mình — như khi
 * có mạng — nếu không, câu hiện lại sẽ mang số cũ mà máy chủ không còn giữ và không bao giờ
 * được gửi lại (màn hình nói có, DB nói không). Trả về hàm huỷ đăng ký.
 *
 * @param {(e: {submissionId: string, keys: string[]}) => void} fn
 */
export function onPrunedKeys(fn) {
  prunedListeners.add(fn);
  return () => prunedListeners.delete(fn);
}

function notifyPruned(submissionId, keys) {
  if (!keys?.length) return;
  prunedListeners.forEach((fn) => {
    try {
      fn({ submissionId, keys });
    } catch (err) {
      console.error('queue pruned listener error', err);
    }
  });
}

/**
 * Số op ĐANG CHỜ GỬI. Không tính op đã hỏng: `flush()` bỏ qua chúng (quy tắc 4),
 * nên gộp chúng vào đây là nói sai — chỉ báo trên thanh tiêu đề sẽ đứng nguyên
 * "Đang đồng bộ (1 thay đổi)" vĩnh viễn và không bao giờ về "Đã đồng bộ".
 *
 * Một chỉ báo không bao giờ đúng thì người dùng sẽ ngừng nhìn nó, và lúc mất dữ
 * liệu thật thì nó cũng đã hết tác dụng cảnh báo.
 */
export async function queueLength(submissionId) {
  const rows = submissionId
    ? await (await getDb()).getAllFromIndex('queue', 'bySubmission', submissionId)
    : await (await getDb()).getAll('queue');
  return rows.filter((o) => !o.failed).length;
}

/** Số op đã hỏng vì lỗi nghiệp vụ — cần người dùng quyết định, không tự khỏi. */
export async function failedLength(submissionId) {
  return (await listFailed(submissionId)).length;
}

/** Op hỏng kèm lỗi server, đủ để hiển thị trong bảng "Chưa đồng bộ được". */
export async function listFailed(submissionId) {
  const db = await getDb();
  const rows = submissionId
    ? await db.getAllFromIndex('queue', 'bySubmission', submissionId)
    : await db.getAll('queue');
  return rows.filter((o) => o.failed).sort((a, b) => a.id - b.id);
}

/**
 * Đề xuất sửa còn nằm trên máy (Q4) — để hồ sơ hiện "chờ mạng" cạnh giá trị, không để
 * điều tra viên tưởng chưa gửi rồi gửi lần nữa. Op đã hỏng hiện ở bảng đồng bộ, không ở đây.
 */
export async function listQueuedChangeRequests() {
  const rows = await (await getDb()).getAll('queue');
  return rows.filter((o) => o.kind === 'changeRequest' && !o.failed).map((o) => o.payload);
}

/**
 * Gửi lại một op đã hỏng. Xoá cờ trước rồi mới `flush()` — thất bại lần nữa thì
 * chính `flush()` đánh dấu lại, nên không có đường nào làm op biến mất.
 *
 * Có ích thật: lỗi hay gặp nhất là 409 vì phiếu đã bị khoá, và sau khi điều phối
 * viên mở lại phiếu thì đúng op đó gửi được ngay.
 */
export async function retryFailed(id) {
  const db = await getDb();
  const op = await db.get('queue', id);
  if (!op) return;
  await db.put('queue', { ...op, failed: false, error: null });
  notify();
  await flush();
}

/** Bỏ hẳn một op hỏng. Dữ liệu trong op đó mất luôn — người dùng tự quyết. */
export async function discardFailed(id) {
  const db = await getDb();
  await db.delete('queue', id);
  notify();
}

/**
 * `changeRequest` không có `submissionId` (đề xuất gắn với Hộ/Vườn/MM, không với phiếu);
 * `link` là hash để bảng đồng bộ mở lại đúng hồ sơ.
 *
 * @param {{submissionId?:string, kind:'patchAnswers'|'submit'|'changeRequest', payload:any, link?:string}} op
 */
export async function enqueue(op) {
  // Chặn ngay tại cửa: một op không gửi được mà lọt vào IndexedDB thì lần sau
  // mở app vẫn còn đó, và người viết ra nó đã không còn ở đây để biết mình sai.
  if (!KNOWN_KINDS.has(op?.kind)) {
    throw new PermanentOpError(`Unknown queue op kind: ${op?.kind}`);
  }
  const db = await getDb();
  await db.add('queue', { ...op, createdAt: new Date().toISOString(), failed: false, error: null });
  notify();
  scheduleFlush();
}

export function scheduleFlush(delayMs = 0) {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flush();
  }, delayMs);
}

function isNetworkError(e) {
  // Kiểm trước mọi thứ khác: op hỏng không bao giờ được coi là lỗi mạng, nếu
  // không nó sẽ chặn cả hàng đợi (xem PermanentOpError).
  if (e instanceof PermanentOpError) return false;
  if (e instanceof TypeError) return true; // fetch network failure
  if (e?.name === 'AbortError') return true;
  if (e instanceof api.ApiError) return e.status === undefined || e.status >= 500;
  return true; // unknown error shape -> treat as transient rather than losing data
}

async function send(op) {
  if (op.kind === 'patchAnswers') {
    return api.submissions.patchAnswers(
      op.submissionId,
      op.payload.answers,
      op.payload.removeKeys,
      op.payload.baseUpdatedAt,
    );
  }
  if (op.kind === 'submit') {
    return api.submissions.submit(op.submissionId, op.payload.overrides);
  }
  if (op.kind === 'changeRequest') {
    // Payload đã mang `clientRequestId`: lần gửi lại sau khi mất kết nối giữa chừng nhận
    // về đúng đề xuất cũ thay vì tạo cái thứ hai.
    return api.changeRequests.create(op.payload);
  }
  throw new PermanentOpError(`Unknown queue op kind: ${op.kind}`);
}

/**
 * Merge adjacent patchAnswers ops for the same submission into one (rule 2). Stops at the
 * first 'submit' op or a different submissionId — never reorders, only collapses a
 * contiguous run.
 */
function mergeAdjacent(ops) {
  const out = [];
  for (const op of ops) {
    const prev = out[out.length - 1];
    if (
      prev &&
      prev.kind === 'patchAnswers' &&
      op.kind === 'patchAnswers' &&
      prev.submissionId === op.submissionId
    ) {
      prev.payload = {
        answers: { ...prev.payload.answers, ...op.payload.answers },
        removeKeys: [...new Set([...(prev.payload.removeKeys || []), ...(op.payload.removeKeys || [])])],
        // Giữ mốc gốc của op ĐẦU trong chuỗi: cả cụm thay đổi này được soạn trên
        // bản đó, nên đó mới là mốc để máy chủ nói được "có ai ghi xen vào không".
        baseUpdatedAt: prev.payload.baseUpdatedAt,
      };
      prev._mergedIds = [...(prev._mergedIds || [prev.id]), op.id];
    } else {
      out.push({ ...op, _mergedIds: [op.id] });
    }
  }
  return out;
}

export async function flush() {
  if (flushing) return;
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return; // hint only, see rule 3
  flushing = true;
  try {
    const db = await getDb();
    const all = (await db.getAll('queue')).filter((o) => !o.failed).sort((a, b) => a.id - b.id);
    const merged = mergeAdjacent(all);
    for (const op of merged) {
      try {
        const res = await send(op);
        for (const id of op._mergedIds) await db.delete('queue', id);
        notify();
        if (op.kind === 'patchAnswers') notifyPruned(op.submissionId, res?.prunedKeys);
      } catch (e) {
        if (isNetworkError(e)) return; // offline again — stop, keep the rest queued
        // Permanent business error (409/422/etc.) — don't retry forever.
        for (const id of op._mergedIds) {
          await db.put('queue', { ...all.find((a) => a.id === id), failed: true, error: describeError(e) });
        }
        notify();
      }
    }
  } finally {
    flushing = false;
  }
}

function describeError(e) {
  if (e instanceof api.ApiError) return { status: e.status, code: e.code, message: e.message };
  if (e instanceof PermanentOpError) return { code: 'UNSUPPORTED_OP', message: e.message };
  return { message: String(e) };
}

if (typeof window !== 'undefined') {
  window.addEventListener('online', () => flush());
  setInterval(() => flush(), 30_000); // safety net — 'online' doesn't always fire
}
